import React from "react";
import { Loader2 } from "lucide-react";

// AIDEV-NOTE: Single source for the connection-issue copy. The inline player
// reuses this next to its own button spinner instead of rendering the
// full indicator component.
export function getConnectionIssueLine({
    isOffline,
    slowNetworkDetected,
}: {
    isOffline: boolean;
    slowNetworkDetected: boolean;
}): string | null {
    if (isOffline) {
        return "You're offline — check your internet connection";
    }
    if (slowNetworkDetected) {
        return "Your connection looks slow or unstable";
    }
    return null;
}

// AIDEV-NOTE: Buffering spinner for meditation players (Margie incident, Jun
// 2026). Renders only while audio is starved of data so users can tell
// buffering apart from intentional meditation silence. The connection line
// renders only when we can plausibly blame the user's connection: the browser
// reports offline, or the slow-network heuristic (2+ stalls in the first 10s
// of playback) has fired. A stalled-but-online stream stays text-free because
// we can't distinguish their network from our servers being slow.
export function MeditationBufferingIndicator({
    isBuffering,
    isOffline,
    slowNetworkDetected,
    className = "",
}: {
    isBuffering: boolean;
    isOffline: boolean;
    slowNetworkDetected: boolean;
    className?: string;
}) {
    if (!isBuffering) {
        return null;
    }

    const connectionLine = getConnectionIssueLine({
        isOffline,
        slowNetworkDetected,
    });

    return (
        <div
            className={`flex flex-col items-center gap-2 ${className}`}
            data-testid="meditation-buffering-indicator"
        >
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
            {connectionLine && (
                <p className="text-xs text-muted-foreground">
                    {connectionLine}
                </p>
            )}
        </div>
    );
}
